import React, { useMemo } from 'react';
import { useData } from '../../store/DataContext';
import { AlertTriangleIcon, BoxesIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, PackageXIcon, PillIcon, TriangleAlertIcon } from 'lucide-react';
import { PharmacySection } from './PharmacyModule';
import { formatCurrency, isExpiringWithin, isLowStock, isOutOfStock, isToday } from './pharmacyUtils';
type Props = {
  onNavigate: (section: PharmacySection) => void;
};
export function PharmacyOverview({
  onNavigate
}: Props) {
  const {
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    encounters
  } = useData();
  const stats = useMemo(() => {
    const lowStock = medicines.filter(isLowStock);
    const outOfStock = medicines.filter(isOutOfStock);
    const expiring = medicineBatches.filter(batch => isExpiringWithin(batch, 90));
    const todayDispensed = stockMovements.filter(movement => movement.type === 'Dispense' && isToday(movement.createdAt));
    const todayReceived = stockMovements.filter(movement => movement.type === 'Receipt' && isToday(movement.createdAt));
    const pendingOrders = purchaseOrders.filter(order => order.status === 'Pending').length;
    const pendingPrescriptions = encounters.filter(encounter => encounter.stage === 'PHARMACY').length;
    return {
      lowStock,
      outOfStock,
      expiring,
      todayDispensed,
      todayReceived,
      pendingOrders,
      pendingPrescriptions,
      value: medicines.reduce((sum, medicine) => sum + medicine.currentQuantity * medicine.costPrice, 0)
    };
  }, [encounters, medicineBatches, medicines, purchaseOrders, stockMovements]);
  const cards = [{
    label: 'Total medicines',
    value: medicines.length,
    icon: PillIcon,
    tone: 'blue'
  }, {
    label: 'Inventory value',
    value: formatCurrency(stats.value),
    icon: CircleDollarSignIcon,
    tone: 'emerald'
  }, {
    label: 'Low stock',
    value: stats.lowStock.length,
    icon: AlertTriangleIcon,
    tone: 'amber'
  }, {
    label: 'Out of stock',
    value: stats.outOfStock.length,
    icon: PackageXIcon,
    tone: 'red'
  }, {
    label: 'Expiring within 90 days',
    value: stats.expiring.length,
    icon: TriangleAlertIcon,
    tone: 'amber'
  }, {
    label: 'Dispensed today',
    value: stats.todayDispensed.length,
    icon: PackageCheckIcon,
    tone: 'teal'
  }, {
    label: 'Received today',
    value: stats.todayReceived.length,
    icon: BoxesIcon,
    tone: 'blue'
  }] as const;
  return <div className="space-y-6" data-id="element-4447">
      <section className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-4448">
        <div data-id="element-4449">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4450">
            BMED Care pharmacy desk
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl" data-id="element-4451">
            Inventory with every dispense accounted for.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-4452">
            Live stock levels, batches, purchase activity, and patient
            dispensing in one operating view.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-4453">
          <button type="button" onClick={() => onNavigate('receive-stock')} className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2" data-id="element-4454">
            <PackageCheckIcon className="mr-1.5 h-4 w-4" data-id="element-4455" /> Receive stock
          </button>
          <button type="button" onClick={() => onNavigate('dispensing')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-4456">
            <ClipboardListIcon className="mr-1.5 h-4 w-4" data-id="element-4457" /> Dispensing queue
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-4458">
        {cards.map(({
        label,
        value,
        icon: Icon,
        tone
      }) => <article key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4459">
            <div className="flex items-start justify-between gap-3" data-id="element-4460">
              <div data-id="element-4461">
                <p className="text-sm font-medium text-slate-500" data-id="element-4462">{label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-4463">
                  {value}
                </p>
              </div>
              <div className={`rounded-lg p-2.5 ${iconTone(tone)}`} data-id="element-4464">
                <Icon className="h-5 w-5" data-id="element-4465" />
              </div>
            </div>
          </article>)}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.85fr]" data-id="element-4466">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4467">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-4468">
            <div data-id="element-4469">
              <h3 className="font-bold text-slate-900" data-id="element-4470">Operational alerts</h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-4471">
                Resolve supply risks before patient care is affected.
              </p>
            </div>
            <button type="button" onClick={() => onNavigate('low-stock')} className="text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-4472">
              Review all alerts
            </button>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-4473">
            <AlertRow label="Out of stock" detail={stats.outOfStock.length ? `${stats.outOfStock.slice(0, 3).map(item => item.name).join(', ')}${stats.outOfStock.length > 3 ? '…' : ''}` : 'No medicines are out of stock.'} count={stats.outOfStock.length} tone="red" onClick={() => onNavigate('low-stock')} data-id="element-4474" />
            <AlertRow label="Reorder soon" detail={stats.lowStock.length ? `${stats.lowStock.slice(0, 3).map(item => item.name).join(', ')}${stats.lowStock.length > 3 ? '…' : ''}` : 'All medicines are above their reorder level.'} count={stats.lowStock.length} tone="amber" onClick={() => onNavigate('low-stock')} data-id="element-4475" />
            <AlertRow label="Expiring within 90 days" detail={stats.expiring.length ? `${stats.expiring.slice(0, 3).map(batch => batch.batchNumber).join(', ')}${stats.expiring.length > 3 ? '…' : ''}` : 'No active batches need expiry action.'} count={stats.expiring.length} tone="amber" onClick={() => onNavigate('expired')} data-id="element-4476" />
          </div>
        </div>
        <aside className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4477">
          <div className="flex items-center justify-between" data-id="element-4478">
            <div data-id="element-4479">
              <h3 className="font-bold text-slate-900" data-id="element-4480">Today’s desk</h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-4481">
                Live operational workload.
              </p>
            </div>
            <ClipboardListIcon className="h-5 w-5 text-slate-400" data-id="element-4482" />
          </div>
          <div className="mt-5 space-y-3" data-id="element-4483">
            <DeskMetric label="Prescriptions waiting" value={stats.pendingPrescriptions} action="Open queue" onClick={() => onNavigate('dispensing')} data-id="element-4484" />
            <DeskMetric label="Pending purchase orders" value={stats.pendingOrders} action="Review orders" onClick={() => onNavigate('purchase-orders')} data-id="element-4485" />
            <DeskMetric label="Stock received today" value={stats.todayReceived.length} action="View history" onClick={() => onNavigate('movement-history')} data-id="element-4486" />
          </div>
        </aside>
      </section>
    </div>;
}
function AlertRow({
  label,
  detail,
  count,
  tone,
  onClick
}: {
  label: string;
  detail: string;
  count: number;
  tone: 'red' | 'amber';
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500" data-id="element-4487">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone === 'red' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-4488">
        <AlertTriangleIcon className="h-4 w-4" data-id="element-4489" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-4490">
        <span className="block text-sm font-semibold text-slate-900" data-id="element-4491">
          {label}
        </span>
        <span className="mt-0.5 block truncate text-sm text-slate-500" data-id="element-4492">
          {detail}
        </span>
      </span>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-4493">
        {count}
      </span>
    </button>;
}
function DeskMetric({
  label,
  value,
  action,
  onClick
}: {
  label: string;
  value: number;
  action: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left hover:border-blue-200 hover:bg-blue-50/40 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4494">
      <span data-id="element-4495">
        <span className="block text-sm font-medium text-slate-700" data-id="element-4496">
          {label}
        </span>
        <span className="mt-0.5 block text-xs text-blue-600" data-id="element-4497">{action}</span>
      </span>
      <span className="text-xl font-bold text-slate-950" data-id="element-4498">{value}</span>
    </button>;
}
function iconTone(tone: string) {
  return {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    teal: 'bg-teal-50 text-teal-700'
  }[tone] || 'bg-slate-100 text-slate-700';
}